import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { GetLocal, GetSession, getIsHosted, history, Navigate, RemoveSession, SaveSession, SaveLocal } from './helpers';
import { alertActions, getSiteInfo, getUserProfileInfo, mapIcimIdtoPlanyoId, setB2bUserDetails, setCountry, setLanguage, setMeetingPlace, setPage, setSite } from './redux/actions';
import PrivateRoute from './routes/PrivateRoute';
import ProtectedRoutes from './routes/ProtectedRoute'; //Authenticated routes
import PublicRoute from './routes/PublicRoute';
import './App.scss';
import routes from './routes/routes';
import { appDefaults, LOGINSCREEN, PLANYO, reactEnv, WRAPPER_URL } from './config';
import { setCountries, setMeetingPlaces, setPhonePrefixes } from './redux/actions/data.actions';
import Spinner from './components/Spinner';
import meetingPlaces from './data/meetingPlace.json';
import countries from './data/countries.json';
import phonePrefixes from './data/phonePrefixList.json';
import AccessDenied3 from './pages/AccessDenied3';
import { debouncer, formatCountries, formatMeetingPlace, formatPhonePrefix, getOpenAtTime, isServerUnderMaintenance, compareTime, reqToken, getCookie, logoutUser, captureICIMUserCode, setCookie } from './services';
import { isValidURL, sanitizeSameOriginAppPath } from './helpers';
import LogInPage from './pages/LogInPage';
import { withTranslation, useTranslation } from 'react-i18next';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { isServer } from './helpers/server.helper';
import SkipToContent from './components/SkipToContent';
import { useLocation } from 'react-router-dom';
import { fetchSitesData } from './services/json.service';
import { bookingTypes, navigationTypes, sessionTypes } from './redux/types';
import { userTypes } from './redux/types';
import { siteTypes } from './constants';
import { fetchLayoutData } from './services/baseLayout.service';
import { ALLOWED_ECOSYSTEM_MPID } from './constants/app.constants';
import { applyLanguageChange } from './helpers/language.helper';
import moment from 'moment';
import 'moment/locale/en-gb';
import 'moment/locale/fi';
import 'moment/locale/it';
import 'moment/locale/es';
import 'moment/locale/pt';
import 'moment/locale/pl';
import 'moment/locale/sk';
import 'moment/locale/sv';
import 'moment/locale/fr';
import 'moment/locale/cs';

// define axios baseURL
axios.defaults.baseURL = WRAPPER_URL;

const App = () => {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const requiresWrapperSession = !PLANYO && appDefaults?.environment !== 'dev';

  const app = useSelector(state => state?.app);
  const authentication = useSelector(state => state?.authentication);
  const booking = useSelector(state => state?.booking);
  const [mobileViewVar, setMobileViewVar] = useState(false);
  const [authReady, setAuthReady] = useState(!requiresWrapperSession);
  const mainRef = useRef();
  const location = useLocation();
  const queryString = new URLSearchParams(location?.search && !isServer ? location?.search : "");
  const icimActionError = queryString.get("error");
  const icimUserCode = queryString?.get("code") || null;
  const reload = queryString.get("reload") === "true";
  const isHosted = getIsHosted(queryString);

  useEffect(() => {
    const language = app?.selectedLanguage?.toLowerCase();
    if (language) {
      moment.locale(language);
    }
  }, [app?.selectedLanguage]);

  useEffect(() => {
    !isServer && document?.getElementById('skip-to-content-container')?.focus();
  }, [history?.location])

  useEffect(() => {
    if (!isServer && reload) {
      const reloadKey = 'app_reload_once';
      const hasReloaded = window.sessionStorage.getItem(reloadKey) === 'true';

      if (!hasReloaded) {
        window.sessionStorage.setItem(reloadKey, 'true');
        window.location.replace(window.location.href);
        return undefined;
      }

      const nextQueryString = new URLSearchParams(location.search || '');
      nextQueryString.delete('reload');
      const nextSearch = nextQueryString.toString();
      const nextUrl = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash || ''}`;

      window.sessionStorage.removeItem(reloadKey);
      window.history.replaceState({}, document.title, nextUrl);
    }

    const listenerFunction = (event) => {
      const sessionBooking = JSON.parse(GetSession('booking') || '{}');
      if (sessionBooking?.paymentReload) {
        if (event.persisted) {
          // the page is restored from browser bfCache
          SaveSession('booking', JSON.stringify({ ...sessionBooking, paymentReload: false }));
          window.location.reload();
        } else {
          // the page is reloaded normally
          SaveSession('booking', JSON.stringify({ ...sessionBooking, paymentReload: false }));
        }
      }

    };
    window.addEventListener('pageshow', listenerFunction);
    const sessionBooking = JSON.parse(GetSession('booking') || '{}');
    if (sessionBooking?.processingPayment) {
      dispatch({ type: bookingTypes?.POPULATE_BOOKING, payload: sessionBooking?.booking })
    }

    if (sessionBooking?.processingPayment && app?.site?.type === siteTypes?.EVENT) {
      SaveSession("isBookingRestored", true);
    }

    // processing icim error and user code
    if (icimActionError === "access_denied") {
      dispatch({ type: sessionTypes?.ICIM_ACTION_ERROR, payload: icimActionError })
      SaveSession("icimActionError", icimActionError);
    }
    if (icimActionError === "server_error") {
      // occurred due to manipulation in icim url
      dispatch({ type: sessionTypes?.APP_ERROR, payload: icimActionError })
      // Navigate(20000, { dispatch, history });
    }

    captureICIMUserCode({ code: icimUserCode, magicLink: false });
    return () => {
      window.removeEventListener('pageshow', listenerFunction);
    };
  }, [])

  useEffect(() => {
    let logoutTimer;
    const handleUserActivity = () => {
      clearTimeout(logoutTimer);

      if (authentication?.loggedIn) {
        logoutTimer = setTimeout(logoutUser, userTypes?.INACTIVE_DURATION, dispatch, app);
      }
    };

    // if (authentication?.loggedIn) {
    //   logoutTimer = setTimeout(logoutUser, userTypes?.INACTIVE_DURATION, dispatch, app);
    // }
    const eventListeners = ['mousemove', 'keydown', 'touchmove']?.map(eventType => {
      const listner = () => handleUserActivity();
      window.addEventListener(eventType, listner);
      return { eventType, listner };
    })

    return () => {
      clearTimeout(logoutTimer);
      eventListeners?.forEach(({ eventType, listener }) => {
        window.removeEventListener(eventType, listener);
      })
    };
  }, [authentication?.loggedIn])

  useEffect(() => {
    let cancelled = false;

    const bootstrapAuth = async () => {
      if (!requiresWrapperSession) {
        setAuthReady(true);
        return;
      }

      try {
        const sessionId = getCookie("session_id") || null;
        if (!sessionId) {
          setAuthReady(false);
          await reqToken();
        }
      } finally {
        if (!cancelled) {
          setAuthReady(true);
        }
      }
    };

    bootstrapAuth();

    return () => {
      cancelled = true;
    };
  }, [requiresWrapperSession, location.pathname]);

  // Sync language & country from URL (e.g. /en-ES/luzshopping/...) whenever the pathname changes.
  // This ensures manual URL edits to the language segment (es-ES -> en-ES) actually switch the language,
  // as long as the language is supported by the country defined in countries.json.
  // The URL's country segment is authoritative here, so we pass it as `targetCountry` to override
  // the helper's fallback lookup. `replaceUrlLanguage` inside the helper is a no-op because the
  // URL already matches the target.
  useEffect(() => {
    if (!location?.pathname) return;
    const firstSeg = location.pathname.split('/')?.[1];
    if (!firstSeg || !firstSeg.includes('-')) return;
    const [urlLang, urlCountry] = firstSeg.split('-');
    if (!urlLang || !urlCountry) return;
    const country = countries?.find(c => c?.code?.toUpperCase() === urlCountry.toUpperCase());
    if (!country) return;
    if (!country?.languages?.includes(urlLang.toLowerCase())) return;

    applyLanguageChange({
      languageCode: urlLang,
      currentLanguage: app?.selectedLanguage,
      currentCountry: app?.country,
      targetCountry: country,
      i18n,
      dispatch,
    });
  }, [location.pathname]);

  useEffect(() => {
    if (!authReady) return undefined;

    const listeners = ['mousemove', 'touchmove'].map((event) => {
      const handler = debouncer(async function () {
        if (!requiresWrapperSession) return;

        const sessionId = getCookie("session_id") || null;
        if (!sessionId) {
          await reqToken();
          return;
        }

        const tokenExpiryTime = GetSession("TokenExpiresIn") || null;
        if (compareTime(tokenExpiryTime) <= 10) {
          await reqToken();
        }
      }, 250, true);

      if (!isServer) {
        window?.addEventListener(event, handler);
      }

      return { event, handler };
    });

    return () => {
      listeners.forEach(({ event, handler }) => {
        window?.removeEventListener(event, handler);
      });
    };
  }, [authReady, requiresWrapperSession]);

  useEffect(() => {
    // clear alert on location change
    history.listen(() => {
      dispatch(alertActions.clear());
    });

    //test API call to check server activity
    isServerUnderMaintenance({ dispatch, history });
    // getConfig({ dispatch, history });

    dispatch(setMeetingPlaces(formatMeetingPlace(meetingPlaces) || []));
    if (!app?.page?.route) {
      if (GetSession("currentPage")) {
        // Adding delay to let current page be set in Session Storage (from navigation hooks)
        setTimeout(() => {
          const page = JSON.parse(GetSession("currentPage") || 'null');
          const meetingPlace = GetSession('meetingPlace') && GetSession('meetingPlace') !== "undefined" ? JSON.parse(GetSession('meetingPlace') || '{}') : null;
          const meetingPlaceUrl = location?.pathname;
          const site = GetSession('site') && GetSession('site') !== "undefined" ? JSON.parse(GetSession('site') || '{}') : null;
          // set the page only when page is there in session and the selected meeting place is same as meeting place saved in session
          const siteType = ['event', 'space', 'service', 'room']?.find(item => meetingPlaceUrl?.includes(item));
          const isNavigateSpaceFromRoom = site?.type === 'room' && siteType === 'space' ? true : false;
          const siteCondition = siteType ? meetingPlaceUrl?.includes(site?.type) : true;
          const isB2bEvent = GetSession('portal') === 'b2b' ? true : false;
          if (isB2bEvent) {
            dispatch({ type: sessionTypes?.PORTAL, payload: 'b2b' })
          }
          if (page && meetingPlaceUrl?.includes(meetingPlace?.id) && siteCondition) {
            if (!isNavigateSpaceFromRoom) {
              dispatch(setPage(page));
            }
          }
        }, 300)
      }
    }
    dispatch(setCountries(formatCountries(countries) || []));
    dispatch(setPhonePrefixes(formatPhonePrefix(phonePrefixes) || []));

    if (!app?.selectedLanguage) {
      let lang = GetSession("selectedLanguage") || appDefaults.language;
      SaveSession("selectedLanguage", lang);
      dispatch(setLanguage(lang));
      const cont = GetSession('country') || JSON.stringify(appDefaults.country);
      i18n.changeLanguage(lang?.toLowerCase() + "-" + JSON.parse(cont)?.code);
    }

    if (!app?.country?.code) {
      const country = JSON.parse(GetSession("country") || JSON.stringify(appDefaults.country));
      dispatch(setCountry(country));
    }
    if (!app?.meetingPlace?.name) {
      if (GetSession("meetingPlace") && GetSession("meetingPlace") !== "undefined") {
        const meetingPlace = JSON.parse(GetSession("meetingPlace") || '{}');
        dispatch(setMeetingPlace(meetingPlace));
      }
    }
    if (!app?.site?.name) {
      if (GetSession("site")) {
        const site = JSON.parse(GetSession("site") || '{}');
        dispatch(setSite(site));
      }
    }
    if (!booking?.selectedResource?.name) {
      if (GetSession("selectedResource")) {
        const selectedResource = JSON.parse(GetSession("selectedResource") || '{}');
        dispatch({ type: "selectedResource", payload: selectedResource });
      }
    }
    if (!booking?.b2bUserDetails) {
      if (GetSession("b2bUserDetails")) {
        const b2bUserDetails = JSON.parse(GetSession("b2bUserDetails") || 'null');
        dispatch(setB2bUserDetails(b2bUserDetails));
      }
    }
    setTimeout(() => {
      dispatch({ type: "pageLoader", payload: false });
    }, 100);

    const queryString = new URLSearchParams(location.search && !isServer ? location.search : "");
    let view = queryString?.get('X-MobileApp-WebView')?.toLowerCase() === 'true' ? true : false;
    setMobileViewVar(view);

    return () => {
      return undefined;
    };
  }, [app.selectedLanguage, app?.meetingPlace?.name])

  useEffect(()=>{
      setCookie("isHosted", isHosted, 86400);
  },[isHosted])
  useEffect(()=>{
    if (!authReady) return;

    if (app?.site?.siteId && app?.meetingPlace?.mpid) {
      getSiteInfo({ siteId: app?.site?.siteId, category: app?.site?.type }, dispatch);
      //commenting as we do not need to call cms api to display header and footer data
      // if (ALLOWED_ECOSYSTEM_MPID?.includes(app?.meetingPlace?.mpid) && isHosted==='true') {
      //   fetchLayoutData({ "locale": app?.selectedLanguage, "mpId": app?.meetingPlace?.mpid }, dispatch)
      // }
    }
    queryString?.delete('isHosted');
  }, [authReady, app?.site?.siteId, app?.meetingPlace?.mpid, app?.selectedLanguage, isHosted])

  /**
   * Description: UseEffect that fires the API call to fetch the ICIM user details upon the availability of ICIM configuration data and when user is not logged in
   * Dependency: {object} an object that contains ICIM configuration data
   */
  useEffect(() => {
    if (!authReady) return;

    const userAuthentication = JSON.parse(GetLocal("authentication") || '{}');
    const userCode = JSON.parse(GetLocal("userCode") || '{}')?.code;
    if (!authentication?.loggedIn) {
      //isAuthUser: authentication variable for test env
      //userAuthentication: ICIM user authentication
      const isAuthUser = LOGINSCREEN ? JSON.parse(GetSession("isAuthUser") || '{}')?.auth : true;
      if ((userAuthentication?.loggedIn || userCode) && isAuthUser) {
        console.log("meetingPlace in session is ", app?.meetingPlace);
        getUserProfileInfo({
          dispatch,
          userAction: "signIn",
          cdcId: authentication?.userDetails?.cdcId,
          userId: authentication?.userDetails?.userId,
        });
      }
    } else if (authentication?.loggedIn && !userCode && !userAuthentication?.loggedIn) {
      dispatch({ type: userTypes.LOGOUT });
      setTimeout(() => {
        Navigate(navigationTypes?.GENERALBOOKINGPAGE, { dispatch, history });
      }, 100)
    }
  }, [authReady, authentication?.loggedIn, app?.meetingPlace.mpid, GetLocal('userCode')])


  /**
   * UseEffect executes when an API response code is 401, i.e., unauthorized
   * It checks for the reload delay in session and reloads the page after applying that specific delay
   * Before reloading, it doubles the previous delay value and stores it in Session
   */
  useEffect(() => {
    if (!PLANYO) {
      if (!GetSession('reloadDelay')) {
        SaveSession('reloadDelay', JSON.stringify({ delay: 500, count: 0 }));
      }
      else {
        const reloadDelay = JSON?.parse(GetSession('reloadDelay') || 'null');
        setTimeout(() => {
          if (GetSession('error') === '401' && !isServer && reloadDelay?.count < 16) {
            RemoveSession('error');
            SaveSession('reloadDelay', JSON.stringify({ delay: reloadDelay?.delay * 2, count: reloadDelay?.count + 1 }));
            // window.location.reload();
          }
        }, parseInt(reloadDelay?.delay))
      }
    }
  }, [GetSession('error')])

  useEffect(() => {
    if (JSON.parse(GetSession("siteInfoDisplay")) && (Object?.keys(app?.meetingPlace)?.length > 0)) {
      let openAtValue = getOpenAtTime();
      dispatch(setMeetingPlace({ ...app?.meetingPlace, ...JSON.parse(GetSession("siteInfoDisplay") || '{}'), openAt: openAtValue }));
    }
  }, [GetSession("siteInfoDisplay")])

  useEffect(() => {
    if (JSON.parse(GetSession("siteInfoDisplay")) && (Object?.keys(app?.meetingPlace)?.length > 0) && !app?.meetingPlace?.openingHours) {
      let openAtValue = getOpenAtTime();
      dispatch(setMeetingPlace({ ...app?.meetingPlace, ...JSON.parse(GetSession("siteInfoDisplay") || '{}'), openAt: openAtValue }));
    }
  }, [app?.meetingPlace?.openingHours])


  useEffect(() => {
    let validURL = isValidURL({ dispatch, i18n, url: history?.location?.pathname })
    if (app?.error || !validURL) {
      Navigate(20000, { dispatch, history });
    } else if (location?.pathname?.includes('qr-action')) {
      Navigate(5000, { dispatch, history });
    }
  }, [history, app?.error])

  useEffect(() => {
    if (app?.meetingPlace?.id && Object.keys(app?.meetingPlace).length > 0 && Object.keys(app?.site).length === 0) {
      const sSite = JSON.parse(GetSession('site'), "{}");
      if (!sSite?.type) { // changed by naveen
        fetchSitesData((res) => {
          const site = res?.find(s => (s?.meetingPlaceId === app?.meetingPlace?.mpid) && s?.type?.toLowerCase() !== 'room');
          if (site) dispatch(setSite(site));
        })
      }
    }
  }, [app?.meetingPlace])

  useEffect(() => {
    if ((authentication?.userDetails?.userId && authentication?.userDetails?.email) && !authentication?.userDetails?.planyoUserId) {
      dispatch(mapIcimIdtoPlanyoId(authentication?.userDetails));
    }
  }, [authentication?.userDetails?.userId, authentication?.userDetails?.email])

  useEffect(() => {
    if (LOGINSCREEN) {
      if (!JSON.parse(GetSession("isAuthUser") || '{}')?.auth && !location?.pathname?.includes('admin-access-denied')) {
        const redirectTo = location.pathname + location.search + location.hash;
        if (!redirectTo?.includes('qr-action')) {
          const safeRedirectTo = sanitizeSameOriginAppPath(redirectTo);

          if (safeRedirectTo) {
            SaveSession("redirectTo", safeRedirectTo);
          }
          Navigate(navigationTypes.LOGIN, { dispatch, history })
        } else {
          SaveSession("isAuthUser", JSON.stringify({ auth: true }));
        }
      }
    }
  }, [JSON.parse(GetSession("isAuthUser") || '{}')?.auth])

  const filteredRoutes = routes?.filter(r => r.isPublicRoute) || [];

  if (!authReady) {
    return <Spinner />;
  }

  return (
    <HelmetProvider>
      <div className="relative" data-testid="App">
        <Helmet htmlAttributes={{ lang: i18n.language }}>

          {/* <!-- OneTrust Cookies Consent Notice start for qa.booking.livat.com --> */}
          {/* {!client && */}
          {reactEnv === "prod" &&
            <>
              <title>{`Booking Tool ${app?.meetingPlace?.name ? ' | ' + app?.meetingPlace?.name : ''}`}</title>
              <meta name="description" content="Booking" />
              <script src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js" data-document-language="true"
                type="text/javascript" charSet="UTF-8" data-domain-script={appDefaults?.oneTrustDomainKey} defer></script>
              {/* <script type="text/javascript">
                function OptanonWrapper() { }
              </script> */}

              <script
                type="text/javascript"
                dangerouslySetInnerHTML={{
                  __html: 'function OptanonWrapper() {}'
                }} />
            </>
          }
          {/* <!-- OneTrust Cookies Consent Notice end for qa.booking.livat.com --></div> */}
        </Helmet>
        {/* <pre>{JSON.stringify(appDefaults, null, 2)}</pre> */}
        {/* App spinner for refresh the page from web browser */}
        {(app?.appLoader) &&
          <Spinner style={{ backgroundColor: "rgba(255, 255, 255, 1)" }} />
        }

        {/* We are using connectRouter here because of redux, in react-router component 
        reload problem is there after changing the route */}
        {(app?.pageLoader || app?.apiLoader || app?.funLoader) &&
          <Spinner />
        }
        <Switch>
          {/* <Route exact path="/404" component={NotFoundPage} /> */}
          <Route exact path='/admin-access-denied' component={AccessDenied3} />
          {/* <Route exact path='/qr-action' component={QRAction} /> */}
          {LOGINSCREEN && <Route exact path='/login' component={LogInPage} />}

          {(LOGINSCREEN ? JSON.parse(GetSession("isAuthUser") || "null")?.auth : true) && filteredRoutes?.map(({ component: Component, path, exact, redirectTo }) => (
            <PublicRoute path={`/${path}`} key={path} exact={exact} >
              {redirectTo ? <Component to={`/${redirectTo}`} /> : <Component />}
            </PublicRoute>
          ))}
          {(LOGINSCREEN ? JSON.parse(GetSession("isAuthUser") || "null")?.auth : true) && (
            <>
              <SkipToContent mainRef={mainRef} />
              <PrivateRoute mobileView={mobileViewVar} path="/" mainRef={mainRef}>
                <ProtectedRoutes />
              </PrivateRoute>
            </>
          )}
        </Switch>
        {/* <CustomCookieConsent /> */}
      </div>
    </HelmetProvider>
  );
};

export default withTranslation()(App);